import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "@/data/store";

import {
  FileParsedQuestion,
  ImportedFileColumn,
  ImportFileStatus,
} from "./types";

export const selectQuestionImportState = (state: RootState) =>
  state.questionImport;

export const selectIsLoading = createSelector(
  [selectQuestionImportState],
  (state) => state.loading
);

export const selectImportFileProgress = createSelector(
  [selectQuestionImportState],
  (state) => ({
    processing: state.processingFiles,
    processed: state.processedFiles,
  })
);

export const selectImportedFiles = createSelector(
  [selectQuestionImportState],
  (state) =>
    state.importedQuestions
      .map((bank: FileParsedQuestion) => {
        let status;
        if (!bank.questions) status = ImportFileStatus.FailedToImport;
        const supportedQuestions = bank.questions?.filter(
          (q) => q.questionType !== null
        );
        status =
          supportedQuestions.length === bank.questions?.length
            ? ImportFileStatus.Success
            : ImportFileStatus.PartialImport;

        return {
          fileId: bank.fileOrigin.fileOriginId,
          filename: bank.fileOrigin.filename,
          filepath: bank.fileOrigin.filepath,
          questions: bank.questions,
          status: status,
        } as ImportedFileColumn;
      })
      .filter(({ questions }) => (questions ?? []).length > 0)
);
export const selectImportedQuestionFileIds = (
  _state: RootState,
  ids: string[] | null
) => ids;

export const fileOriginsByIds = createSelector(
  [selectQuestionImportState, selectImportedQuestionFileIds],
  (questionBanks, fileOriginIds) =>
    questionBanks.importedQuestions
      .filter(({ fileOrigin }) =>
        fileOriginIds?.includes(fileOrigin.fileOriginId)
      )
      .map(({ fileOrigin }) => fileOrigin)
);

export const selectSupportedImportedFiles = createSelector(
  [selectImportedFiles, selectImportedQuestionFileIds],
  (importedFiles, bankIds) =>
    importedFiles
      .filter((file) => bankIds && file.fileId && bankIds.includes(file.fileId))
      .map(
        (file) =>
          ({
            ...file,
            questions: file.questions.filter((q) => q.questionType !== null),
          }) as ImportedFileColumn
      )
      .filter(({ questions }) => questions.length > 0)
);

export const selectHasImportedFiles = createSelector(
  [selectQuestionImportState],
  (state) => state.importedQuestions.length > 0
);

export const selectImportedQuestionFileId = (_state: RootState, id: string) =>
  id;

export const selectQuestionsByFileIds = createSelector(
  [selectQuestionImportState, selectImportedQuestionFileId],
  (state, bankIds) =>
    state.importedQuestions
      .filter(
        (bank) => bankIds && bankIds.includes(bank.fileOrigin.fileOriginId)
      )
      .flatMap((bank) => bank.questions)
);

export const selectSupportedQuestionsByFileIds = createSelector(
  [selectQuestionImportState, selectImportedQuestionFileIds],
  (state, fileIds) =>
    state.importedQuestions
      .filter(
        (bank) => fileIds && fileIds.includes(bank.fileOrigin.fileOriginId)
      )
      .flatMap((bank) => bank.questions.filter((q) => q.questionType !== null))
);

export const selectQuestionsByFileId = createSelector(
  [selectQuestionImportState, selectImportedQuestionFileId],
  (state, fileId) =>
    state.importedQuestions.find((q) => q.fileOrigin.fileOriginId === fileId)
      ?.questions
);

export const selectSupportedQuestionsByFileId = createSelector(
  [selectQuestionImportState, selectImportedQuestionFileId],
  (questionBanks, fileId) =>
    questionBanks.importedQuestions
      .find((q) => q.fileOrigin.fileOriginId === fileId)
      ?.questions?.filter((q) => q.questionType !== null)
);
export const selectFileIds = createSelector(
  [selectImportedFiles],
  (importedFiles) => importedFiles.map(({ fileId }) => fileId)
);

export const selectFileName = createSelector(
  [selectQuestionImportState, selectImportedQuestionFileId],
  (state, fileId) =>
    state.importedQuestions.find(
      (bank) => bank.fileOrigin.fileOriginId === fileId
    )?.fileOrigin.filename
);

export const selectAlltags = createSelector(
  [selectQuestionImportState],
  (state) => state.allTags
);

export const selectTagsByFileId = createSelector(
  [selectQuestionImportState, selectImportedQuestionFileId],
  (state, fileId) => {
    const importedQuestionTags = state.importedQuestions.find(
      (q) => q.fileOrigin.fileOriginId === fileId
    )?.tags;

    return importedQuestionTags ?? [];
  }
);
